import { useContext, createContext } from 'react';

import { trackAction } from '@ctct/distui-helpers';

import { stepsEnum } from './utils';
import { CREDIT_CARD } from '../../constants/paymentTypes';

export const initialUpgradeState = {
  isLoading: null,
  triedToUpgrade: false,
  upgradeInfo: null,
  wrapClassName: null,
  upgradeRating: null,
  upgradeCurrency: null,
  upgradeContactCount: 0,
  upgradeIsTrialer: null,
  upgradeIsSuspended: null,
  upgradeReturnURL: null,
  upgradeReturnLabel: null,
  upgradeFormattedCustomerInfo: null,
  upgradeBillingInfo: null,
  confirmProceedCallback: null,
  comparePlanBlocks: null,
  paymentSuccessCallback: null,
  isTwoStepDialog: null,
  stepOneInformation: null,
  upgradeStep: stepsEnum.UPGRADE,
  currency: 'USD',
  contactTiers: null,
  contactTiersLabels: null,
  selectedPlanIdFromCompare: null,
  emailPlan: null,
  contactTierIndex: null,
  currentPlanId: null,
  rating: null,
  paymentPeriod: 1,
  paymentPlanGroupId: null,
  discountCode: null,
  planNameDescriptionOnly: null,
  checkout: {
    gettingCheckoutOffer: false,
    validationCalls: {},
    customerInfo: {},
    allCustomerEmails: [],
    selectedPaymentType: CREDIT_CARD,
    creditCardData: null,
    payPalData: null,
    amazonPayData: {},
    eCheckData: null,
    countries: [],
    updateTax: false,
    reSciErrorMessage: null,
    amazonPayEnabled: null,
  },
  discountAmount: 0,
  addOnRatings: [],
  allAddOns: null,
  smsStartingPrice: null,
  passedInDiscountCode: null,
  selectedSMSTier: undefined,
  payingForPlan: false,
  nextChargeScheduledDateDisplayString: null,
  recommendedUpgradePlanPromotion: null, // non-null for legacy upgrades
  isSingleBill: false,
  isStripeAccount: false,
  stripeToken: null,
  stripePortalURL: null,
};

export function upgradeReducer(state = initialUpgradeState, action) {
  switch (action.type) {
    case 'setUpgradeStep': {
      trackAction({
        action: `Feature Upgrade Dialog, advance to step ${action.value}`,
      });

      return {
        ...state,
        upgradeStep: action.value,
      };
    }
    case 'setIsLoading': {
      return {
        ...state,
        isLoading: action.value,
      };
    }
    case 'setstepOneInformation': {
      return {
        ...state,
        stepOneInformation: action.value,
      };
    }
    case 'setComparePlanBlocks': {
      return {
        ...state,
        comparePlanBlocks: action.value,
      };
    }
    case 'setNextChargeScheduledDateDisplayString': {
      return {
        ...state,
        nextChargeScheduledDateDisplayString: action.value,
      };
    }
    case 'setIsTwoStepDialog': {
      return {
        ...state,
        isTwoStepDialog: action.value,
      };
    }
    case 'setUpgradeRating': {
      return {
        ...state,
        upgradeRating: action.value,
      };
    }
    case 'setUpgradeIsTrialer': {
      return {
        ...state,
        upgradeIsTrialer: action.value,
      };
    }
    case 'setUpgradeIsSuspended': {
      return {
        ...state,
        upgradeIsSuspended: action.value,
      };
    }
    case 'setSelectedPlanIdFromCompare': {
      return {
        ...state,
        selectedPlanIdFromCompare: action.value,
        upgradeInfo: {
          ...state.upgradeInfo,
        },
      };
    }
    case 'setUpgradeCurrency': {
      return {
        ...state,
        upgradeCurrency: action.value,
      };
    }
    case 'setPayingForPlan': {
      return {
        ...state,
        payingForPlan: action.value,
      };
    }
    case 'setUpgradeReturnURL': {
      return {
        ...state,
        upgradeReturnURL: action.value,
      };
    }
    case 'setUpgradeReturnLabel': {
      return {
        ...state,
        upgradeReturnLabel: action.value,
      };
    }
    case 'setUpgradeContactCount': {
      return {
        ...state,
        upgradeContactCount: action.value,
      };
    }
    case 'setConfirmProceedCallback': {
      return {
        ...state,
        confirmProceedCallback: action.value,
      };
    }
    case 'setUpgradeFormattedCustomerInfo': {
      return {
        ...state,
        upgradeFormattedCustomerInfo: action.value,
      };
    }
    case 'setPaymentSuccessCallback': {
      return {
        ...state,
        paymentSuccessCallback: action.value,
      };
    }
    case 'setUpgradeBillingInfo': {
      return {
        ...state,
        upgradeBillingInfo: action.value,
      };
    }
    case 'setUnsavedChangesInCallingPage': {
      return {
        ...state,
        unsavedChangesInCallingPage: action.value,
      };
    }
    case 'setWrapClassName': {
      return {
        ...state,
        wrapClassName: action.value,
      };
    }
    case 'setPlanNameDescriptionOnly': {
      return {
        ...state,
        planNameDescriptionOnly: action.value,
      };
    }
    case 'setUpgradeInfo': {
      let descriptionNameOnly = '';

      if (action?.value?.recommendedPlanType === 'GOOD') {
        // this should never happen
      } else {
        descriptionNameOnly = (action?.value?.recommendedPlanDescription ?? '').replace('Constant Contact - ', '');
      }

      return {
        ...state,
        planNameDescriptionOnly: descriptionNameOnly,
        upgradeInfo: {
          ...action.value,
        },
      };
    }
    case 'setContactTiers': {
      return {
        ...state,
        contactTiers: action.contactTiers,
      };
    }
    case 'setPaymentPlanGroupId': {
      return {
        ...state,
        paymentPlanGroupId: action.paymentPlanGroupId,
      };
    }
    case 'setDiscountData': {
      return {
        ...state,
        discountCode: action.discountCode,
        discountAmount: action.discountAmount,
      };
    }
    case 'setDiscountCode': {
      return {
        ...state,
        discountCode: action.discountCode,
      };
    }
    case 'selectedSMSTier': {
      return {
        ...state,
        selectedSMSTier: action.selectedSMSTier,
      };
    }
    case 'setAddOnRatings': {
      return {
        ...state,
        addOnRatings: action.addOnRatings,
      };
    }
    case 'setAllAddOns': {
      return {
        ...state,
        allAddOns: action.allAddOns,
      };
    }
    case 'setSmsStartingPrice': {
      return {
        ...state,
        smsStartingPrice: action.smsStartingPrice,
      };
    }
    // checkout values
    case 'setAddedCheckoutOffer': {
      return { ...state, checkout: { ...state.checkout, addedCheckoutOffer: action.addedCheckoutOffer } };
    }
    case 'setOfferRemovedAlert': {
      return {
        ...state,
        checkout: {
          ...state.checkout,
          offerRemovedAlert: action.offerRemovedAlert,
          offerAddedAlert: false,
        },
      };
    }
    case 'setPassedInDiscountCode': {
      return { ...state, passedInDiscountCode: action.passedInDiscountCode };
    }
    case 'setPlanId': {
      return { ...state, currentPlanId: action.currentPlanId };
    }
    case 'setRecommendedUpgradePlanPromotion': {
      return { ...state, recommendedUpgradePlanPromotion: action.value };
    }
    case 'setIsSingleBill': {
      return { ...state, isSingleBill: action.value };
    }
    case 'setIsStripeAccount': {
      return { ...state, isStripeAccount: action.value };
    }
    case 'setStripeToken': {
      return { ...state, stripeToken: action.value };
    }
    case 'setStripePortalURL': {
      return { ...state, stripePortalURL: action.value };
    }
    default:
      return state;
  }
}

export const UpgradeContext = createContext();

export const useUpgradeContext = () => useContext(UpgradeContext);
